/**
 * Copyright 2023 AutoZone, Inc.
 * Content is confidential to and proprietary information of AutoZone, Inc., its
 * subsidiaries and affiliates.
 */
import React, { Fragment, useMemo } from 'react';
import { type ContentStackSeo } from '../../interface';
import Head from 'next/head';
import { countryCodes } from '@/constants/locale';
import type { ContentStackAlternateURL } from '../../interface/models';
import { metaTagKey } from '@/constants/seoMetaTags';

type Props = {
  seoData: ContentStackSeo;
};

type FormattedSEOData = {
  defaultURL: ContentStackAlternateURL | null;
  alternateURLs: ContentStackAlternateURL[];
  hasAlternateOgTitle: boolean;
};

export function CMSSeoTags({ seoData }: Props) {
  const { defaultURL, alternateURLs, hasAlternateOgTitle } = useMemo(() => {
    const result: FormattedSEOData = {
      defaultURL: null,
      alternateURLs: [],
      hasAlternateOgTitle:
        seoData.attributes?.some((attribute) => attribute.meta_name === 'og:title') ?? false,
    };

    for (const item of seoData.alternate_urls ?? []) {
      if (item.url_locale === countryCodes.enUS) {
        result.defaultURL = item;
      } else {
        result.alternateURLs.push(item);
      }
    }

    return result;
  }, [seoData]);

  return (
    <Head>
      <title key={metaTagKey('title')}>{seoData.page_title}</title>
      <link key={metaTagKey('canonical')} rel="canonical" href={seoData.canonical_url} />
      <meta key={metaTagKey('description')} name="description" content={seoData.meta_description} />
      {!hasAlternateOgTitle && (
        <meta key={metaTagKey('og:title')} property="og:title" content={seoData.page_title} />
      )}
      {seoData.attributes?.map((attribute) => (
        <Fragment key={attribute.meta_name}>
          {attribute.meta_name === 'robots' ? (
            <meta
              key={metaTagKey('robots')}
              name={attribute.meta_name}
              content={attribute.meta_value}
            />
          ) : (
            <meta property={attribute.meta_name} content={attribute.meta_value} />
          )}
        </Fragment>
      ))}
      {defaultURL ? (
        <link
          key="linkAlternateDefault"
          rel="alternate"
          hrefLang="x-default"
          href={defaultURL.alternate_url}
        />
      ) : null}
      {alternateURLs.map((item) => (
        <link
          key={`linkAlternateObj-${item.url_locale}`}
          rel="alternate"
          hrefLang={item.url_locale}
          href={item.alternate_url}
        />
      ))}
    </Head>
  );
}
